export interface PrecacheResult {
  cached: number;
  failed: number;
}

/**
 * Asks the active service worker (public/sw.js) to eagerly fetch and cache
 * everything needed to run the app offline — including the lazily-loaded
 * editor bundle, which otherwise wouldn't be cached until the user actually
 * opens a document.
 */
export function precacheForOffline(): Promise<PrecacheResult> {
  return new Promise((resolve, reject) => {
    if (!("serviceWorker" in navigator)) {
      reject(new Error("Service workers aren't supported in this browser."));
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      reject(
        new Error(
          "Offline caching only runs in a production build (npm run build) — not the dev server, so reloading won't help here."
        )
      );
      return;
    }

    const controller = navigator.serviceWorker.controller;
    if (!controller) {
      reject(
        new Error(
          "The offline service worker isn't active yet — reload the page once, then try again."
        )
      );
      return;
    }

    const channel = new MessageChannel();
    channel.port1.onmessage = (event: MessageEvent) => {
      const data = event.data as
        | { type: "PRECACHE_DONE"; cached: number; failed: number }
        | { type: "PRECACHE_ERROR"; message: string }
        | undefined;

      if (data?.type === "PRECACHE_DONE") {
        resolve({ cached: data.cached, failed: data.failed });
      } else if (data?.type === "PRECACHE_ERROR") {
        reject(new Error(data.message || "Failed to cache files for offline use."));
      } else {
        reject(new Error("Unexpected response from the service worker."));
      }
    };

    controller.postMessage({ type: "PRECACHE_ALL" }, [channel.port2]);
  });
}
